import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Button, Form, Input, Image, message } from "antd";
import { ArrowLeftOutlined, KeyOutlined, MailOutlined } from "@ant-design/icons";
import get from "lodash/get";

import LandingHeader from "../../components/landingHeader";
import { forgotPassword } from "../../redux/actions/authActions";
import { DASHBOARD, LOGIN } from "../../constants/routes";
import { COLOR_PRIMARY } from "../../constants";
import { FORGOT_PASSWORD_SUCCESS } from "../../constants/message";
import emailIcon from "../../static/icons/message.svg";

const RESEND_COOLDOWN_SECONDS = 30;
const STEP_REQUEST = "request";
const STEP_SENT = "sent";

const IconBadge = ({ icon }) => (
  <div
    className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-2"
    style={{ backgroundColor: `${COLOR_PRIMARY}14`, color: COLOR_PRIMARY }}
  >
    {icon}
  </div>
);

const ForgotPasswordScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => get(state, "auth.isLoggedIn"));

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(STEP_REQUEST);
  const [email, setEmail] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (isLoggedIn) {
      navigate(DASHBOARD);
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timeoutId = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timeoutId);
  }, [cooldown]);

  const sendResetLink = (registeredEmail) => {
    setLoading(true);
    dispatch(forgotPassword({ email: registeredEmail }))
      .then(() => {
        setLoading(false);
        if (step === STEP_SENT) {
          message.success(FORGOT_PASSWORD_SUCCESS);
        }
        setEmail(registeredEmail);
        setStep(STEP_SENT);
        setCooldown(RESEND_COOLDOWN_SECONDS);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onFinish = (values) => sendResetLink(get(values, "email"));

  const onUseDifferentEmail = () => {
    setStep(STEP_REQUEST);
    setCooldown(0);
  };

  return (
    <div className="w-full justify-center items-center bg-grey-8 forgot-password-grid-lines h-full bg-[#F7F8F9] ">
      <LandingHeader />
      <div className="justify-center flex mt-20 sm:p-0 p-5">
        <div className="w-[423px] bg-white shadow rounded-xl">
          <div className="sm:p-10 p-5">
            {step === STEP_REQUEST ? (
              <>
                <div className="flex flex-col items-center space-y-2 pb-10">
                  <IconBadge icon={<KeyOutlined />} />
                  <p className="text-black text-xl leading-[30px] font-medium text-center">
                    Forgot Password?
                  </p>
                  <p className="text-[#8B8D97] text-sm font-normal text-center">
                    Enter your registered email and we'll send you a link to
                    reset your password
                  </p>
                </div>

                <Form
                  className="form-input"
                  name="forgotPassword"
                  layout="vertical"
                  requiredMark={false}
                  size="large"
                  initialValues={{ email }}
                  onFinish={onFinish}
                  onFinishFailed={() => { }}
                >
                  <Form.Item
                    name="email"
                    label=""
                    rules={[
                      { required: true, message: "Please input your registered Email!" },
                      { type: "email", message: "Please input valid email" },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Registered Email"
                      autoFocus
                      prefix={<Image src={emailIcon} alt="email" preview={false} />}
                    />
                  </Form.Item>

                  <div className="flex flex-col items-center gap-5 mt-3">
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={loading}
                      style={{ height: 44, width: 180, borderRadius: 12 }}
                    >
                      Send Reset Link
                    </Button>
                    <Link
                      to={LOGIN}
                      className="flex items-center gap-2 text-sm font-medium text-black-1 transition-colors hover:text-primary"
                    >
                      <ArrowLeftOutlined className="text-xs" />
                      Back to Login
                    </Link>
                  </div>
                </Form>
              </>
            ) : (
              <div className="flex flex-col items-center text-center">
                <IconBadge icon={<MailOutlined />} />
                <p className="text-black text-xl leading-[30px] font-medium mt-2">
                  Check your email
                </p>
                <p className="text-[#8B8D97] text-sm font-normal mt-2">
                  We've sent a password reset link to{" "}
                  <span className="font-medium text-black-2 break-all">{email}</span>.
                  Follow the link in the email to set a new password.
                </p>

                <div className="w-full bg-grey-2 rounded-xl px-4 py-3 mt-8 text-sm text-black-1">
                  Didn't receive it? Check your spam folder or{" "}
                  {cooldown > 0 ? (
                    <span className="text-grey-3">resend in {cooldown}s</span>
                  ) : (
                    <button
                      type="button"
                      disabled={loading}
                      onClick={() => sendResetLink(email)}
                      className="font-medium text-primary hover:underline disabled:opacity-60"
                    >
                      {loading ? "sending..." : "resend link"}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={onUseDifferentEmail}
                  className="text-sm text-grey-3 mt-4 transition-colors hover:text-primary"
                >
                  Use a different email
                </button>

                <Button
                  type="primary"
                  className="mt-8"
                  onClick={() => navigate(LOGIN)}
                  icon={<ArrowLeftOutlined />}
                  style={{ height: 44, width: 180, borderRadius: 12 }}
                >
                  Back to Login
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
